import React, { useState, useEffect } from 'react'
import { useUIStore } from '../store/ui-store'
import { usePlayerStore } from '../store/player-store'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

interface Friend {
  id: string
  name: string
  chips: number
  level: number
}

export default function FriendsScreen(): React.ReactElement {
  const { navigateTo, showToast } = useUIStore()
  const { token, profile } = usePlayerStore()
  const [friends, setFriends] = useState<Friend[]>([])
  const [addId, setAddId] = useState('')

  const fetchFriends = async (): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/friends`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (json.success) setFriends(json.data)
    } catch {}
  }

  useEffect(() => {
    fetchFriends()
  }, [])

  const handleAddFriend = async (): Promise<void> => {
    if (!addId.trim()) return
    try {
      const res = await fetch(`${API_BASE}/friends/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ friendId: addId.trim() }),
      })
      const json = await res.json()
      if (json.success) {
        showToast('Friend added!', 'success')
        setAddId('')
        fetchFriends()
      } else {
        showToast(json.error || 'Failed to add', 'error')
      }
    } catch {
      showToast('Failed to add friend', 'error')
    }
  }

  const handleRemoveFriend = async (friendId: string): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE}/friends/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ friendId }),
      })
      const json = await res.json()
      if (json.success) {
        showToast('Friend removed', 'info')
        fetchFriends()
      }
    } catch {}
  }

  return (
    <div className="lobby">
      <div className="lobby-header">
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={() => navigateTo('lobby')}>
          ← Back
        </button>
        <div className="lobby-title">Friends</div>
        <div style={{ width: 60 }} />
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            placeholder="Enter player ID to add"
            value={addId}
            onChange={e => setAddId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddFriend()}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: 14,
            }}
          />
          <button className="btn btn-gold" onClick={handleAddFriend} style={{ padding: '10px 16px', fontSize: 12 }}>
            Add
          </button>
        </div>

        {profile && (
          <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>
            Your ID: <span style={{ fontFamily: 'monospace', color: '#888' }}>{profile.id}</span>
          </div>
        )}
      </div>

      <div style={{ marginTop: 20 }}>
        {friends.length === 0 ? (
          <div className="flex-center" style={{ padding: 40, color: '#666', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 40 }}>🤝</div>
            <div>No friends yet</div>
            <div style={{ fontSize: 12, color: '#555' }}>Share your ID to connect!</div>
          </div>
        ) : (
          friends.map(f => (
            <div key={f.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: 'rgba(255,255,255,0.05)', borderRadius: 12, marginBottom: 8,
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{f.name}</div>
                <div style={{ fontSize: 11, color: '#888' }}>Lv.{f.level} · 🪙 {f.chips.toLocaleString()}</div>
              </div>
              <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => handleRemoveFriend(f.id)}>
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
